import { Core } from "@faire/design-tokens";
import { LayoutElementBasedCarousel } from "@faire/retailer-visitor-shared/components/LayoutElementRenderer/LayoutElementBasedCarousel";
import { useSearchPageIsFromDiscoverURL } from "@faire/retailer-visitor-shared/views/search-v2/SearchPageQueryParams/useSearchPageIsFromDiscoverURL";
import { useSearchPageParsedSearch } from "@faire/retailer-visitor-shared/views/search-v2/SearchPageQueryParams/useSearchPageParsedSearch";
import { Column } from "@faire/web--source/slate/Layout";
import { LoadingSpinner } from "@faire/web--source/slate/LoadingSpinner";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { useQuery_escape_hatch as useDiscoverPageLayoutQuery } from "@faire/web-api--source/endpoints/www/api/layout/discover-page/post-hooks";
import { IDiscoverPageLayoutRequest } from "@faire/web-api--source/indigofair/data/IDiscoverPageLayoutRequest";
import React, { useMemo } from "react";
import styled from "styled-components";
import { ProductTileImpressionProvider } from "@faire/retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/__internal__/SearchPageHeaderAndResults/__internal__/SearchPageHeader/__internal__/ProductTileImpressionsProvider";

export const SearchPageFeaturedProducts = () => {
  const { isMobile } = useViewport();
  const parsedSearch = useSearchPageParsedSearch();
  const featuredProductToken = useMemo(
    () => getRefPGivenRecordOfQueryParams(parsedSearch),
    [parsedSearch]
  );
  const isDiscover = useSearchPageIsFromDiscoverURL();
  const enabled = !!featuredProductToken && isDiscover;

  const {
    data: discoverPageLayoutData,
    isPending: isDiscoverPageLayoutLoading,
  } = useDiscoverPageLayoutQuery(
    IDiscoverPageLayoutRequest.build({
      featured_product_token: featuredProductToken,
    }),
    { enabled: enabled }
  );

  if (!enabled) {
    return null;
  }

  const requestId = discoverPageLayoutData?.request_id ?? "";
  const element = discoverPageLayoutData?.layout_elements[0];

  if (isDiscoverPageLayoutLoading) {
    return (
      <LoadingSpinnerWrapper>
        <LoadingSpinner />
      </LoadingSpinnerWrapper>
    );
  } else {
    if (element) {
      return (
        <>
         <ProductTileImpressionProvider
            requestId={requestId}
            onClickEventName={element.on_click_event_name}
            featuredProductToken={featuredProductToken}
          >
          <Spacer height="4x" />
          <LayoutElementBasedCarousel
            layoutElement={element}
            requestId={requestId}
            leftAlignTitle
            noWrap
            showArrowCTA={isMobile}
            itemsPerSlide={{
              mobileAndAbove: 2,
              tabletAndAbove: 4,
              desktopAndAbove: 5,
              xLargeAndAbove: 6,
            }}
            recordAdImpression
          />
          <Spacer height="4x" />
          <StyledTitle>
            <StrictLocalMsg
              defaultMessage="All products"
              description={{
                text: "Title for a section of product tiles on a landing page",
              }}
            />
          </StyledTitle>
          </ProductTileImpressionProvider>
        </>
      );
    } else {
      return null;
    }
  }
};

const getRefPGivenRecordOfQueryParams = (
  parsedSearch: Record<string, string | undefined>
) => {
  return parsedSearch.refP;
};

const LoadingSpinnerWrapper = styled(Column).attrs({
  align: "center",
  justify: "center",
})`
  height: 400px;
  width: 100%;
`;

const StyledTitle = styled(Typography).attrs({
  variant: "pageHeaderSerifRegular",
  truncate: true,
  color: Core.text.default,
})`
  padding-left: 0;
  word-break: break-word;
  white-space: normal;
  text-align: left;
`;
